import { ElementRef, useRef, useState } from 'react'

import { Heading, MediaQueries, theme } from '@hhgtech/hhg-components'
import { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import { Text, themeColors } from '@hhgtech/hhg-components/mantine'
import { AspectRatio, Box, Container, Flex } from '@mantine/core'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { formatArticle, formatExpertSummary } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { checkMobile } from 'api/utils/validators'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import { _Breadcrumb } from 'components/pages/expert/_breadcrumb'
import _ExpertListContainer from 'components/pages/expert/_expertListContainer'
import { _Search } from 'components/pages/expert/_search'
// import { _Search } from 'components/pages/expert/_search'
import { NavigatorLayout } from 'components/templates/navigatorLayout'
import { LOCALE_SPECS, assetImagePath } from 'interfaces/constants'
import {
  Article,
  ArticleApi,
  ExpertHeading,
  ExpertUserSummary,
  ExpertUserSummaryApi,
  ExtendedGetServerSideProps,
  FooterInfo,
} from 'interfaces/types'
import { WithPagination } from 'interfaces/types/Utilities'
import { ExpertStoreProvider } from 'state/expert'
import { getFooterDataPersist } from 'utils/isomorphicCache/footerData'
import { getNavMenuContentPersist } from 'utils/isomorphicCache/navMenuContent'
type Props = {
  isMobileSsr: boolean
  expertList: WithPagination<{ experts: Array<ExpertUserSummary> }>
  expertPosts: Article[]
  siteName: string
  maxPage: number
  expertMaxPage: number
  expertHeading: ExpertHeading
  footerInfo?: FooterInfo
  dataNavMenuContent?: NavMenuContentType
}

const ExpertPage = ({
  siteName,
  expertHeading,
  expertMaxPage,
  expertList,
  footerInfo,
  dataNavMenuContent,
}: Props) => {
  const [isTopBarAppears, setIsTopBarAppears] = useState(false)
  const { formatMessage: f } = useIntl()
  const expertListRef = useRef<ElementRef<typeof _ExpertListContainer>>(null)

  const onTopBarAppears = (b: boolean) => {
    setIsTopBarAppears(b)
  }

  return (
    <ExpertStoreProvider
      init={{
        expertList,
        expertMaxPage,
      }}
      refreshKey={String(expertList?.experts?.length || '')}
    >
      <NavigatorLayout
        title={f({ id: 'experts.seoTitle' }) + ' - ' + siteName}
        description={f({ id: 'experts.description' })}
        image={`${assetImagePath}/bookDoctor.svg`}
        onTopBarAppears={onTopBarAppears}
        data-is-top-bar={isTopBarAppears}
        SEOData={{
          isIndexable: true,
          schemaData: f({ id: 'experts.seoSchema' }),
        }}
        type="profile"
        footerInfo={footerInfo}
        dataNavMenuContent={dataNavMenuContent}
      >
        <Container fluid p={0} mb={40} bg={themeColors.blue[0]}>
          <_Breadcrumb />
          <Container fluid p={0} bg={themeColors.blue[0]}>
            <Container size="lg" pt={40}>
              <Flex justify="space-between" w="100%">
                <Box mb={{ base: 16, md: 0 }}>
                  <Heading tag="h2">{expertHeading?.title}</Heading>
                </Box>
                <AspectRatio
                  ratio={213 / 124}
                  w={213}
                  sx={{ img: { objectFit: 'contain' } }}
                >
                  <img
                    src={
                      expertHeading?.banner ||
                      `${assetImagePath}/experts-icon.svg?v=1`
                    }
                    alt="expert"
                    loading="lazy"
                  />
                </AspectRatio>
              </Flex>
            </Container>
          </Container>
          <Container
            fluid
            sx={{
              borderRadius: '20px 20px 0 0',
              [MediaQueries.mbUp]: { borderRadius: '40px 40px 0 0' },
            }}
            bg={theme.colors.white}
          >
            <Container size="lg" pt={24} px={{ base: 0, md: 16 }} pb={0}>
              <Text as="div" size="p3">
                <div
                  style={{ whiteSpace: 'pre-wrap' }}
                  dangerouslySetInnerHTML={{
                    __html: expertHeading?.content || '',
                  }}
                ></div>
              </Text>
            </Container>
            <_Search
              my={{ base: 16, md: 0 }}
              onSearch={() => expertListRef?.current?.setCurrentPage(1)}
            />
            <_ExpertListContainer ref={expertListRef} />
          </Container>
          <MobileBottomNavigationWrapper />
        </Container>
      </NavigatorLayout>
    </ExpertStoreProvider>
  )
}

export const getServerSideProps: ExtendedGetServerSideProps = async (
  context,
) => {
  const { req, locale = 'en-PH' } = context
  const userAgent = `${req.headers['user-agent']}`

  const isMobile = checkMobile(userAgent)

  const results = await Promise.all([
    callApiNew(PATHS.EXPERT.GET_ALL_EXPERTS_PAGING, {
      params: {
        page: 1,
        perPage: 10,
      },
    }),
    callApiNew(PATHS.EXPERT.GET_EXPERT_POST, {
      params: {
        page: 1,
        perPage: 100,
      },
    }),
    callApiNew(PATHS.EXPERT.GET_ALL_EXPERTS_HEADING),
    getFooterDataPersist(),
    getNavMenuContentPersist(locale),
  ])

  const [
    allExperts,
    expertPostsApi,
    expertHeading,
    footerInfo,
    dataNavMenuContent,
  ] = results

  const siteName = LOCALE_SPECS[locale].SITE_NAME_FORMATTED

  const expertList = {
    experts: allExperts?.data?.experts?.map((expert: ExpertUserSummaryApi) =>
      formatExpertSummary(expert),
    ),
    pagination: allExperts?.data?.paging,
  } as WithPagination<{ experts: Array<ExpertUserSummary> }>

  const expertPosts: Article[] =
    expertPostsApi &&
    expertPostsApi.data?.articles &&
    expertPostsApi.data?.articles.map((post: ArticleApi) => formatArticle(post))

  return {
    props: {
      isMobileSsr: isMobile,
      expertList: expertList || [],
      expertMaxPage: allExperts?.data?.paging?.max_num_pages || 1,
      expertPosts,
      siteName,
      maxPage: expertPostsApi?.data?.paging?.max_num_pages || 1,
      expertHeading: expertHeading?.data?.[0] || null,
      footerInfo: footerInfo?.data || null,
      dataNavMenuContent: dataNavMenuContent || {},
    },
  }
}

export default ExpertPage
